// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// A machine, played.
//
// Everything a client does that is not drawing pixels or reading a keyboard. It owns the loaded
// machine, the `Game` that sequences it and the `SimulationDriver` that paces it, and it turns a
// frame of held keys into the presses, plunges and nudges a player made.
//
// **It was the harness's, and it is not harness-shaped.** It lived in `src/harness/session.ts`
// through M3, which was right while the harness was the only thing with a renderer and wrong the
// moment the site shell needed to put a machine on the landing route: a shipping client that
// imported from `harness/` would have made the diagnostic instrument a dependency of the
// product. What stayed behind is genuinely the harness's — the bat-tuning A/B, the geometry
// report, slow motion — and `HarnessSession` extends this rather than forking it, so there is
// one implementation of the thing both are trying to measure.
//
// Three things here are load-bearing:
//
//  1. **The driver advances the game; nothing else may.** `frame()` hands the driver the real
//     elapsed time and a callback, and the callback is the only place `Game.advance()` is
//     called. The renderer gets an interpolation alpha and no way to move anything.
//  2. **Buttons are edge-detected.** `Game.pressButton` closes the cabinet switch on the press,
//     and NEON NOVA's skill shot rotates the lit lane on that switch. Passing the held state
//     every frame would rotate it sixty times a second and make the shot unaimable.
//  3. **Input is applied once, before the frame's steps.** A press therefore lands on an exact
//     step boundary rather than partway through a burst (PH-024).

import { loadMachine } from '@core/machine'
import type { LoadedMachine } from '@core/machine'
import { DEFAULT_INITIALS, Game } from '@core/game'
import type { GameSnapshot } from '@core/game/Game'
import { STEP_HZ } from '@core/sim/constants'
import { DisplayDriver } from '../fx/gameDisplay'
import { DEFAULT_START_HINT } from '../fx/dmd'
import { PresentationContacts } from '../engine/contactFeed'
import { PresentationEvents } from '../engine/eventFeed'
import { ComponentLightingState } from '../world/componentLightingState'
import { recordLightingEvent } from '../world/lightingEvents'
import type { Kicker } from '@core/sim/devices/Kicker'
import { SimulationDriver } from '../engine/SimulationDriver'
import type { MusicState } from '../audio/sequencer'
import type { PlayControls } from '../input/desktop'
import { replayTrace } from '@core/replay'
import type { ReplayResult, ReplayTrace, TraceAction, TraceEvent } from '@core/replay'

/** Lines kept in the notice list. Oldest fall off the bottom. */
export const MAX_NOTES = 14

/** The playfield's extent, in its own metres. What a 2-D view fits itself to. */
export interface PlayfieldBounds {
  readonly minX: number
  readonly minY: number
  readonly maxX: number
  readonly maxY: number
}

export interface PlaySessionOptions {
  readonly playerCount?: number
  readonly seed?: number
  /**
   * Where initials entry opens, when it should not be 'AAA' (M2-19a).
   *
   * Handed to the `Game` and written into the trace, which is the only way a remembered value can
   * cross into a simulation a server has to reproduce. See `GameOptions.startingInitials`.
   */
  readonly startingInitials?: string
  /** Start in attract mode instead of putting a ball up (08 §1's landing route does). */
  readonly attract?: boolean
  /**
   * How this host says "launch the ball", for a display's `{startHint}` token.
   *
   * Passed in rather than measured here, because the same session runs in a browser, on a phone
   * and under a test harness, and only the caller knows which. Absent means the neutral wording —
   * a session that says nothing shows something true everywhere rather than a wrong instruction.
   */
  readonly startHint?: string
  /**
   * Told every action a player takes, with the simulation step it landed on (M3-39).
   *
   * The recorder's hook. Optional because a session with nobody recording must cost nothing —
   * the harness has no use for a trace and the arcade's attract tiles certainly do not — and
   * because a trace is a *product* feature layered on the session rather than a thing the
   * session is for.
   */
  readonly onAction?: (step: number, action: TraceAction) => void
  /**
   * Called after every simulation step, with the game (M3-39).
   *
   * A *step* hook rather than a frame hook, and the distinction is the whole reason it exists. A
   * checkpoint taken once a frame lands at step 12,016 on a phone and 12,004 on a laptop, so two
   * recordings of the same game carry hashes that cannot be compared with each other at all —
   * and comparing them is what a checkpoint is for. Asked per step, the recorder can stop on an
   * exact multiple everywhere.
   *
   * One optional call against a step that already does a world solve, a rule pass and a switch
   * scan. It is not the expensive thing in this loop.
   */
  readonly onStep?: (game: Game) => void
  /**
   * A game is about to begin on the pristine simulation, and the press that begins it is about
   * to be recorded at step 0. Whoever records the trace resets it **here**, not on the next frame:
   * a reset one frame later would throw that press away, and the trace would begin with a game
   * nothing started — which is exactly how every score after the first of a session became
   * unvalidatable until 1 Sep 2026.
   */
  readonly onNewGame?: (game: Game) => void
  /**
   * Seconds a game may wait for the player (a ball in the shooter lane, or initials asked for)
   * with no input before it stops counting steps. Zero turns the hold off; absent means
   * `DEFAULT_WAITING_HOLD_SECONDS`.
   *
   * **A waiting game kept stepping, and every step was replayed at the next start**: a ball left
   * in the lane for twelve minutes came back on the headset as a 180,040-step replay. Holding is
   * the driver's ordinary pause, so nothing is recorded and nothing the game decides changes, and
   * any input carries on. The headset's `PlaySession` has done this since 1.0.228; this is its copy.
   */
  readonly waitingHoldSeconds?: number
}

/** The default for `PlaySessionOptions.waitingHoldSeconds`, the headset's. */
export const DEFAULT_WAITING_HOLD_SECONDS = 30

/** What a frame did. */
export interface SessionFrame {
  readonly stepsRun: number
  readonly stepsDropped: number
  /** How far past the last completed step the picture sits, 0…1. The renderer needs this. */
  readonly alpha: number
  /** The simulation is stopped — by the player, or by a performance interruption. */
  readonly paused: boolean
  /**
   * Why it is stopped, if it stopped itself. Empty when the player asked for it.
   *
   * PH-023's other half: a machine that cannot keep up must **say so** rather than carry on
   * losing simulation, because the loss is invisible and what the player experiences is a ball
   * that behaved strangely and then drained.
   */
  readonly interruption: string
}

/** How often playback keeps a snapshot for scrubbing. Five seconds of play. */
export const GHOST_SNAPSHOT_STEPS = 5_000

/** How a ghost is opened (M8-19). Both default off, which is M4-21's scrub-bar playback. */
export interface GhostOptions {
  /** Keep this session's own game, to be given its machine back by `endGhost`. */
  readonly keepLive?: boolean
  /** Compare the trace's checkpoints as playback passes them. */
  readonly verify?: boolean
}

/**
 * Are two recorded actions the same action?
 *
 * Used on one event — the last one already applied — to tell "more of the trace I am playing"
 * from "a different trace of the same length". Missing on either side is not the same: a shorter
 * trace than the one being played is not an extension of it.
 */
function sameAction(a: TraceEvent | undefined, b: TraceEvent | undefined): boolean {
  if (a === undefined || b === undefined) return false
  if (a.step !== b.step || a.action.kind !== b.action.kind) return false
  const left = a.action
  const right = b.action
  // **A switch with no default, so the compiler names the next action that needs an answer here.**
  // It was a chain of `if`s ending in `return false`, which is the right answer for two actions
  // that differ and the wrong one for a kind nobody had added a branch for — and it answers
  // silently, in a function whose whole job is to tell "more of this trace" from "a different
  // trace". `skip` was exactly that: it arrived, matched on kind, matched no branch, and reported
  // that a stream a spectator was already following had become a different one.
  switch (left.kind) {
    case 'flipper':
      return right.kind === 'flipper' && left.side === right.side && left.down === right.down
    case 'launch':
      return right.kind === 'launch' && left.power === right.power
    case 'nudge':
      return right.kind === 'nudge' && left.ix === right.ix && left.iy === right.iy
    case 'skip':
      // It carries nothing, so two of them on the same step are the same action.
      return right.kind === 'skip'
    case 'credit':
      return right.kind === 'credit'
  }
}

/**
 * The first trace event at or after a step.
 *
 * Needed because a restored snapshot says where the *simulation* is and nothing about where the
 * *input* is. Deriving one from the other is exactly the mistake chunked validation made: an
 * event's step is the world's step index and a step count counts `advance()` calls, and the two
 * drift apart whenever the world is not stepping. Here the trace is in hand and the answer is a
 * search rather than an inference.
 */
export function eventIndexAt(trace: ReplayTrace, step: number): number {
  let index = 0
  while (index < trace.events.length && trace.events[index]!.step < step) index++
  return index
}

export class PlaySession {
  /** Replaced wholesale by `reset()`, which is what rebuilding a machine physically is. */
  loaded: LoadedMachine
  game: Game

  readonly driver = new SimulationDriver()

  /**
   * Physics events, held for whatever is drawing (M3-11).
   *
   * Installed here rather than by the view, because it is the session that replaces the world
   * and so the only thing that can reliably re-install the tap — and because the feed is not the
   * camera's: the audio layer and the headset's haptics read the same impact speeds, and all
   * three want them whether or not a 3-D view is open. It is one branch and one typed-array
   * write per event against a million steps a second, so it is always on.
   */
  readonly events = new PresentationEvents()
  lighting!: ComponentLightingState
  private readonly lightingEvent: PresentationEvents['record'] = (type, device, ball, step, value) => {
    recordLightingEvent(this.lighting, this.loaded, type, device, ball, step, value)
  }

  /**
   * Ball contacts, held for whatever is listening (M3-24).
   *
   * A second feed rather than a second event type: a contact is not a switch closing, and the
   * rule engine must never be handed a vocabulary a real machine's switch matrix does not have.
   */
  readonly contacts = new PresentationContacts()

  /**
   * Playfield x of every ball that drained during the last frame.
   *
   * Reused across frames and cleared at the start of each, so nothing may retain it. A drain is
   * a `Game` decision rather than a physical event — the simulation has no opinion about a ball
   * below the apron — so it reaches presentation by being collected here rather than by a feed.
   */
  readonly drained: number[] = []

  /**
   * Callout ids the rules asked for during the last frame (M3-27).
   *
   * Collected the way drains are, and for the same reason: a `speech` effect is the *game*
   * speaking, so it arrives from `Game` rather than from the physics feed.
   */
  readonly spoke: string[] = []

  /**
   * Cue ids the rules asked for during the last frame (M3-29).
   *
   * The other half of the `speech` collection above, and the larger one: NEON NOVA raises forty
   * `sound` effects across its rulesheet and every one of them reached a log line and nothing
   * else.
   */
  readonly sounded: string[] = []

  /** The performance-interruption notice, or empty. Set by `frame`, cleared on resume. */
  interruption = ''

  /**
   * The game was waiting for the player and nothing was touched for the hold time, so the driver
   * is paused until something is. Any input carries on and is then taken as usual.
   */
  heldForThePlayer = false

  /** How many times this session has held a waiting game. */
  holds = 0

  /** Seconds the game has waited for the player with no input. */
  private waitedFor = 0

  /**
   * The machine's display, driven by the game (M3-17).
   *
   * Rebuilt with the machine, because a display's scenes come out of the definition. Held here
   * rather than in the renderer for the reason `MachineScene` states: the DMD is driven by
   * *rules* — a jackpot animation is a rule firing — so it belongs on the game's side of the
   * line, and the renderer reads the raster it produces.
   */
  display: DisplayDriver
  readonly bounds: PlayfieldBounds
  /** Most recent first. What a log panel or a notice line reads. */
  readonly notes: string[] = []

  /** Mode ids, from the definition. The order is the definition's, so `musicState` is stable. */
  private readonly modeIds: string[]
  /** The bytes the machine is built from. Replaced only by `rebuild` (M13-10). */
  private doc: unknown
  private readonly options: PlaySessionOptions
  /**
   * The wording handed to every `DisplayDriver` this session builds.
   *
   * Held as a field because the driver is rebuilt three times — construction, `reset`, and the
   * start of a ghost — and a hint read only at construction would quietly revert to the neutral
   * wording the first time a player pressed reset.
   */
  private readonly startHint: string
  /** How much of `game.log` has already been shown. */
  private logCursor = 0
  private leftHeld = false
  private rightHeld = false
  /** How much of `game.drainedAt` has already been reported. */
  private drainCursor = 0

  /**
   * The simulation as it was the moment it was built: step 0, the seed's own random stream, the
   * balls in the trough, every device at rest. Every game begins by restoring it, because that is
   * the game a validator constructs to re-simulate the trace — a second game played on from the
   * first game's world starts at the first game's step with its random stream already spent, and
   * no replay from a fresh construction can reach it.
   */
  private pristine: GameSnapshot

  // --- ghost playback (M4-21) -------------------------------------------------------------
  //
  // A ghost is the same machine, the same core and the same trace the validator runs — played
  // back rather than played. It is deliberately *not* a recording of positions: a recording would
  // be a second description of the game, would be enormous, and would drift from the simulation
  // the moment the physics changed. Re-simulating is exact and costs a few bytes per flip.
  private ghostTrace: ReplayTrace | undefined = undefined
  private ghostStep = 0
  private ghostEvent = 0
  /**
   * Fractional steps carried between frames.
   *
   * Rounding each frame's step count on its own loses the remainder every time, and the loss is
   * systematic rather than random: sixty frames of a 60 Hz screen at double speed is 33.33 steps
   * each, rounds to 33, and arrives at 1,980 steps instead of 2,000. One per cent slow, for ever,
   * and it shows up as a scrub bar that disagrees with the picture and a replay that ends late.
   * The live simulation's driver accumulates for exactly this reason; so does this.
   */
  private ghostRemainder = 0
  /**
   * Snapshots along the way, so scrubbing backwards does not start from zero.
   *
   * Kept every `GHOST_SNAPSHOT_STEPS`, which is five seconds of play. Without them a scrub to the
   * eight-minute mark of a ten-minute replay would re-simulate eight minutes — about 1.4 seconds
   * of arithmetic on a laptop and several on a phone, with the page frozen throughout. With them
   * the worst case is five seconds of simulation, which is a few milliseconds.
   */
  private readonly ghostMarks = new Map<number, GameSnapshot>()
  /**
   * The live game, set aside while a ghost plays over the top of it (M8-19).
   *
   * **A spectator's own game has to survive being a spectator.** In an online game a player's
   * machine holds their ball two waiting behind the ball one they have already played, and
   * watching somebody else's turn on that same machine — which is what a real arcade is — must not
   * throw it away. `beginGhost` builds a whole new machine, so the old one is kept rather than
   * rebuilt: rebuilding would be a *different* game, with the same seed and none of the play.
   */
  private stash: { loaded: LoadedMachine; game: Game; display: DisplayDriver } | undefined
  /** Compare the trace's checkpoints as playback passes them (M8-19). */
  private ghostVerify = false
  /** The first step at which this client's re-simulation disagreed with the player's, or −1. */
  private ghostDiverged = -1

  constructor(doc: unknown, options: PlaySessionOptions = {}) {
    this.doc = doc
    this.options = options
    this.startHint = options.startHint ?? DEFAULT_START_HINT
    this.loaded = loadMachine(doc)
    this.lighting = new ComponentLightingState(this.loaded.definition)
    this.events.afterRecord = this.lightingEvent
    this.game = this.newGame()
    this.pristine = this.game.snapshot()
    this.display = new DisplayDriver(this.loaded.definition, this.startHint)
    this.modeIds = (this.loaded.definition.rules?.modes ?? []).map((mode) => mode.id)
    this.loaded.world.events.tap = this.events.record
    this.loaded.world.contactTap = this.contacts.record

    const { width, length } = this.loaded.definition.dimensions.playfield
    this.bounds = { minX: -width / 2, minY: -length / 2, maxX: width / 2, maxY: length / 2 }

    // Started through the recorded path, never through `Game.startGame()` directly: a game the
    // host starts behind the trace's back is a game whose replay sits in attract until the first
    // plunge, and then serves a ball the player never saw served.
    if (options.attract !== true) this.startGame(0)
  }

  /**
   * Begin a game, the way the launch button begins one: on the pristine simulation, with the
   * press recorded at step 0 as the trace's own first action.
   *
   * This is the only way a game starts on this session. The cabinet's start button and the
   * launch button both arrive here through `applyInput` when `Game.launchWouldStart` says the
   * press would begin one; a host that wants a game running without a press (the browser's
   * landing page, the headset's start panel) calls it directly.
   */
  startGame(power = 0): void {
    this.beginAFreshGame()
    this.record({ kind: 'launch', power })
    this.pressLaunch(power)
  }

  /**
   * Give up the game in progress and leave the machine in attract (M8-80).
   *
   * **The player pressed an armed, confirmed, destructive row, and it has to have a consequence.**
   * "End this game" arms and confirms on both clients and, until now, did neither client's version
   * of ending anything: the browser cleared the *saved resume record* and showed a notice while the
   * ball played on, and the wrist menu said a toast and hid a board. A confirmation with no
   * consequence is indistinguishable from a lost press — this repository has recorded that once
   * already, about this very row, from the other end.
   *
   * **This is not a core call, and the distinction is the whole design.** `machineRuntime`'s note
   * puts it exactly: *"a game that ended by a route the validator has never seen is a score nobody
   * can check"*, so `Game` has no "abandon" and must not grow one. Nothing here ends a game. It
   * **replaces** it with the machine's own starting state — the pristine snapshot every game
   * already begins from since M8-68 — so no game reaches game over, no score is written, and
   * nothing is ever submitted for a validator to disagree with. The abandoned trace goes the same
   * way, through `onNewGame`, which is what stops the next game inheriting it: that inheritance is
   * the defect M8-68 was, and reaching attract by any route that skipped it would bring it back.
   *
   * **On the same objects**, which is what makes this shippable on the headset. See
   * `beginAFreshGame`: a rebuilt session would leave the view posing devices nobody plays, and
   * `PlaySession.Reset` refuses outright for an adopted machine. Restoring costs nothing and
   * touches no renderer.
   *
   * The high-score table survives, as it does across every fresh game — it belongs to the machine
   * rather than to the game somebody gave up on.
   */
  abandonGame(): void {
    this.beginAFreshGame()
  }

  /**
   * Put the simulation back to the moment it was built, on the same objects.
   *
   * The same `Game` and the same `World`, restored rather than replaced, because the renderer
   * closes over the world's devices (M8-26) and the runtime holds the game — a new object would
   * leave them posing and reading a machine nobody plays. The high-score table is the one thing
   * kept across the restore: it belongs to the machine, not to the game, and the pristine
   * snapshot predates every game that earned a row on it.
   *
   * Then everything this session remembers about the previous game is dropped — the same list
   * `resume` clears, for the same reason — the display starts its scene timing again from step
   * 0, and the host is told **before** the starting press is recorded, so a recorder reset there
   * keeps that press rather than losing it. The presentation feeds are left alone: their readers
   * hold cursors, and a cursor past the end of a cleared feed reads nothing until it catches up.
   */
  private beginAFreshGame(): void {
    const game = this.game
    const table = game.highScores.map((row) => ({ ...row }))
    game.restore(this.pristine)
    this.lighting.reset()
    game.highScores.length = 0
    game.highScores.push(...table)
    this.drainCursor = game.drainedAt.length
    this.logCursor = game.log.length
    this.leftHeld = false
    this.rightHeld = false
    this.driver.reset()
    this.interruption = ''
    this.display.restart()
    this.options.onNewGame?.(game)
  }

  /**
   * What the score needs to know about the game (M3-28, 07 §5).
   *
   * Read off the rule engine rather than tracked here, because every one of these is a fact the
   * engine already holds and a second copy would be a second thing to keep right. The running
   * mode is the *first* active one: 07 §5's intro is about a mode starting, and NEON NOVA never
   * runs two at once except when SUPERNOVA stacks over multiball — which is why multiball is
   * checked on its own rather than being read out of this.
   */
  get musicState(): MusicState {
    const engine = this.game.engine
    let mode = ''
    for (const id of this.modeIds) {
      if (engine.isModeActive(id)) {
        mode = id
        break
      }
    }
    return {
      playing: this.game.phase === 'ballInPlay' || this.game.awaitingLaunch,
      attract: this.game.phase === 'attract' || this.game.isOver,
      starCore: engine.vars.get('starCore', 0),
      multiball: engine.isModeActive('novaMultiball'),
      supernova: engine.isModeActive('supernova'),
      mode,
    }
  }

  /** What 07 §7's mixing rules need beyond what the audio layer already knows (M3-29). */
  get mixState(): { multiball: boolean; tilted: boolean } {
    const bob = this.game.tilt
    return {
      multiball: this.ballsInPlay > 1,
      tilted: bob !== undefined && (bob.tilted || bob.slammed),
    }
  }

  /**
   * Rebuild the machine from *different* bytes and start over (M13-10).
   *
   * Build mode's seam. The headset's draft is committed by making it the session's own machine
   * — the same `reset` on a new document — so the picture (`MachineScene.patch`) and the
   * simulation are one object graph, and Test play runs the real core on exactly what the author
   * laid out. The old document is the caller's to keep and hand back: leaving the mode is
   * `rebuild(original)`.
   *
   * **Not `resume`, not a snapshot.** A rebuilt machine is a fresh construction at step 0, which is
   * the only state a replay can start from; nothing in flight survives it, and nothing should — a
   * ball resting against a post that has just moved is a ball in a machine that no longer exists.
   */
  rebuild(doc: unknown): void {
    this.doc = doc
    this.reset()
  }

  /** Rebuild the machine from the same bytes and start over. */
  reset(): void {
    this.loaded = loadMachine(this.doc)
    this.lighting = new ComponentLightingState(this.loaded.definition)
    this.game = this.newGame()
    this.pristine = this.game.snapshot()
    // A new definition means new scenes; a driver holding the old machine's would be showing one
    // machine's animations over another machine's score.
    this.display = new DisplayDriver(this.loaded.definition, this.startHint)
    // A new `World` has a new queue with no tap on it, and the events still in the feed are
    // stamped with steps from a game that no longer exists.
    this.loaded.world.events.tap = this.events.record
    this.events.clear()
    this.loaded.world.contactTap = this.contacts.record
    this.contacts.clear()
    this.drained.length = 0
    this.spoke.length = 0
    this.sounded.length = 0
    this.drainCursor = 0
    this.driver.reset()
    this.interruption = ''
    this.notes.length = 0
    this.logCursor = 0
    this.leftHeld = false
    this.rightHeld = false
    if (this.options.attract !== true) this.startGame(0)
    this.afterReset()
    this.note('machine reset')
  }

  /**
   * Fast-forward to the end of a recorded trace (M3-39).
   *
   * **A resumed game is the same game, not a similar one.** Re-running the input trace is what
   * makes that true, and it is why *resuming* uses no snapshot: a snapshot restored into the
   * player's machine would be a second description of the simulation to keep in step with the
   * first, and the day it drifts is the day somebody's resumed game scores differently from the
   * one they left. Re-running has one description and cannot drift.
   *
   * There **is** a snapshot format now — M4-28a added one so a two-hour replay can be validated
   * across several Edge Function invocations, and `seekGhost` below uses it to scrub. Neither of
   * those is a resume: both re-derive the snapshot from the trace on the spot rather than trusting
   * one that was stored days ago beside a game it might no longer describe.
   *
   * The session must have been built with `attract: true`, because starting the game is the
   * trace's own first action — a replay reproduces what the *player* did, not what a host did
   * for them.
   *
   * Everything the fast-forward produced along the way is then thrown out: a resumed game must
   * not shout four hundred callouts, flash nine minutes of lamp events and print the whole log
   * on the frame it comes back.
   */
  resume(trace: ReplayTrace): ReplayResult {
    const result = replayTrace(this.game, trace, { applyFinalInputs: true })
    if (result.refused !== undefined) {
      this.note(`could not resume: ${result.refused}`)
      return result
    }
    this.game.drainEffects()
    this.lighting.reset()
    this.events.clear()
    this.contacts.clear()
    this.drained.length = 0
    this.spoke.length = 0
    this.sounded.length = 0
    this.drainCursor = this.game.drainedAt.length
    this.logCursor = this.game.log.length
    this.leftHeld = false
    this.rightHeld = false
    // Keep the input ledger at the replayed state, including paused release events.
    for (let i = 0; i < result.nextEvent; i++) {
      const action = trace.events[i]!.action
      if (action.kind !== 'flipper') continue
      if (action.side === 'left') this.leftHeld = action.down
      else this.rightHeld = action.down
    }
    // The driver has been standing still while the game ran; its accumulator would otherwise
    // spend the whole fast-forward as one enormous frame the moment play restarts.
    this.driver.reset()
    this.note(`resumed at ball ${this.game.currentBall}`)
    this.syncLighting(true)
    return result
  }

  /**
   * Apply a frame of input and advance the simulation to cover `elapsedSeconds`.
   *
   * The order is input, then steps, then collection — a press made during this frame is seen by
   * every step of it, which is what makes a tap reproducible.
   */
  frame(elapsedSeconds: number, controls: PlayControls): SessionFrame {
    this.drained.length = 0
    this.spoke.length = 0
    this.sounded.length = 0
    // Read before `applyInput` takes the latches. A held game carries on *before* the input is
    // applied, so a start press during initials takes its letter rather than only waking it.
    const touched =
      controls.leftFlipper !== this.leftHeld ||
      controls.rightFlipper !== this.rightHeld ||
      controls.nudges.length > 0 ||
      controls.launch ||
      controls.start ||
      controls.skipInitials ||
      controls.coin ||
      controls.togglePause
    if (touched) this.waitedFor = 0
    if (this.heldForThePlayer && touched && !controls.togglePause) {
      this.heldForThePlayer = false
      this.driver.resume()
      this.interruption = ''
      this.note('carried on — the player is back')
    }
    this.applyInput(controls)

    if (controls.togglePause) {
      controls.togglePause = false
      if (this.driver.isPaused) {
        this.driver.resume()
        this.interruption = ''
        this.note('resumed')
      } else {
        this.driver.pause()
        this.note('paused')
      }
    }

    const game = this.game
    // **One step, then stopped again** (M9-18). Consumed like `togglePause` and for the same
    // reason: it is a latched request from outside, and the loop is the only thing that may
    // advance the world. Ignored unless the machine is stopped — the driver refuses it — because
    // a step squeezed in beside a running frame loop is a millisecond nobody asked for.
    if (controls.singleStep) {
      controls.singleStep = false
      this.driver.stepOnce(() => {
        const pumped = game.phase === 'ballInPlay'
        game.advance()
        this.options.onStep?.(game)
        if (!pumped) return
        for (const id of game.matrix.closedThisStep) this.note(id)
      })
    }
    const state = this.driver.advance(elapsedSeconds * this.timeScale(controls), () => {
      // Captured before the step, because the phase can change during it: a ball draining on
      // this step leaves `phase` at `bonus`, and the switches it closed on the way down are
      // still real.
      const pumped = game.phase === 'ballInPlay'
      game.advance()
      this.options.onStep?.(game)
      if (!pumped) return
      for (const id of game.matrix.closedThisStep) this.note(id)
    })

    // The display, once a frame, from the game itself. Before the notes are collected, so a
    // scene a rule asked for on this frame is on the glass in the same frame the log names it.
    this.display.render(game, game.step)

    // PH-023. The driver reports sustained lost time; the decision to stop is here, because only
    // this layer knows whether a ball is in play. Between balls there is nothing to steal, so a
    // machine catching its breath during an attract loop says nothing at all.
    //
    // **A ball waiting in the shooter lane is one of those times, and `phase` does not say so.**
    // `awaitingLaunch` lives inside `ballInPlay`, so without this the guard covers a machine that
    // is doing nothing but waiting for a player. It shows up most sharply on a resume, where the
    // fast-forward lands in `ballInPlay` on the same frame the renderer is still compiling its
    // shaders: the machine paused itself over a lost quarter-second before the player had seen a
    // single frame, let alone lost anything to it.
    //
    // Cleared rather than ignored, because the driver latches its report — see `clearStall`.
    const idle = game.phase !== 'ballInPlay' || game.awaitingLaunch
    this.holdIfNobodyIsThere(game, elapsedSeconds)
    if (idle) this.driver.clearStall()
    else if (state.stalled && !state.paused) {
      this.driver.pause()
      this.interruption =
        `performance interruption — ${this.driver.lostSteps.toLocaleString()} ms of play was lost.` +
        ` ${this.resumeHint()}`
      this.note(this.interruption)
    }

    this.collectNotes()
    this.collectDrains()
    return {
      stepsRun: state.stepsRun,
      stepsDropped: state.stepsDropped,
      alpha: state.alpha,
      paused: this.driver.isPaused,
      interruption: this.interruption,
    }
  }

  // --- ghost playback ---------------------------------------------------------------------

  /**
   * Put the session into playback (M4-21).
   *
   * The machine is rebuilt from the same bytes with the trace's own seed and player count, because
   * a replay reproduces the game that was played and not one like it. Nothing is simulated here —
   * `ghostFrame` does the work, so opening a two-hour replay is instant and the first frame is on
   * screen before any of it has been run.
   */
  beginGhost(trace: ReplayTrace, options: GhostOptions = {}): void {
    // **Set aside rather than overwritten** when the caller says the live game is still wanted.
    // Only on the way in: a second `beginGhost` while already watching — which is what the turn
    // passing does — must not stash the *previous* ghost over the real game.
    if (options.keepLive === true && this.stash === undefined) {
      this.stash = { loaded: this.loaded, game: this.game, display: this.display }
    }
    this.ghostVerify = options.verify === true
    this.ghostDiverged = -1
    this.ghostTrace = trace
    this.ghostStep = 0
    this.ghostEvent = 0
    this.ghostRemainder = 0
    this.ghostMarks.clear()
    this.loaded = loadMachine(this.doc)
    this.lighting = new ComponentLightingState(this.loaded.definition)
    this.game = new Game(this.loaded.definition, this.loaded.world, {
      seed: trace.seed,
      playerCount: trace.playerCount,
      ballsPerGame: trace.ballsPerGame,
      // The watched player's, not this one's (M2-19a). A ghost is a re-simulation of somebody
      // else's game, and a spectator who substituted their own remembered initials would diverge
      // at the first checkpoint after the table opened.
      ...(trace.startingInitials === undefined
        ? {}
        : { startingInitials: trace.startingInitials }),
    })
    this.loaded.world.events.tap = this.events.record
    this.loaded.world.contactTap = this.contacts.record
    this.display = new DisplayDriver(this.loaded.definition, this.startHint)
    this.driver.reset()
    this.ghostMarks.set(0, this.game.snapshot())
  }

  /**
   * Swap in a longer copy of the trace being played, without restarting it (M8-19).
   *
   * What a live spectator does four times a second: the trace is growing under the playback, and
   * `beginGhost` would rebuild the machine and start the ball again. Everything the playback holds
   * — the step, the event cursor, the snapshots — stays exactly where it is, because the new trace
   * is the old one plus more.
   *
   * **Refused rather than trusted when the prefix is not the same trace.** The check is the header
   * plus the last action already applied, and that pairing is deliberate: the header catches a
   * different game, and the boundary event catches a trace that has been rewritten underneath —
   * which is the case that would otherwise play on happily and diverge with nothing to say why.
   * Comparing all of it would be an O(n) walk four times a second to re-prove something the
   * assembler already guarantees.
   */
  extendGhost(trace: ReplayTrace): boolean {
    const held = this.ghostTrace
    if (held === undefined) return false
    if (
      held.seed !== trace.seed ||
      held.machineId !== trace.machineId ||
      held.machineVersion !== trace.machineVersion ||
      held.coreVersion !== trace.coreVersion ||
      held.playerCount !== trace.playerCount ||
      held.ballsPerGame !== trace.ballsPerGame ||
      // **Compared through the default both sides resolve it to** (M2-19a), so a sender that
      // upgrades mid-game and starts naming 'AAA' explicitly is not read as a different game —
      // the same rule `sameTrace` in `traceStream.ts` follows for the same field. Left out, this
      // header check would accept a trace rewritten to open on different characters, which is
      // exactly the case the boundary event below exists to catch.
      (held.startingInitials ?? DEFAULT_INITIALS) !== (trace.startingInitials ?? DEFAULT_INITIALS)
    ) {
      return false
    }
    if (trace.events.length < this.ghostEvent) return false
    const boundary = this.ghostEvent - 1
    if (boundary >= 0 && !sameAction(held.events[boundary], trace.events[boundary])) return false
    this.ghostTrace = trace
    return true
  }

  /**
   * Stop playing back, and give the live game its machine back (M8-19).
   *
   * Everything the playback produced on its way out is thrown away for the reason a scrub throws
   * it away: the machine must not fire a minute of somebody else's callouts and lamp events at the
   * moment it becomes this player's again.
   */
  endGhost(): void {
    this.ghostTrace = undefined
    this.ghostMarks.clear()
    this.ghostRemainder = 0
    this.ghostStep = 0
    this.ghostEvent = 0
    this.ghostVerify = false
    this.ghostDiverged = -1
    const stash = this.stash
    if (stash === undefined) return
    this.stash = undefined
    this.loaded = stash.loaded
    this.lighting = new ComponentLightingState(this.loaded.definition)
    this.game = stash.game
    this.display = stash.display
    // The taps point at the *world*, and the world just changed back. Without this the machine
    // would run silently: every event would be recorded into a world nobody is rendering.
    this.loaded.world.events.tap = this.events.record
    this.loaded.world.contactTap = this.contacts.record
    this.driver.reset()
    this.game.drainEffects()
    this.events.clear()
    this.contacts.clear()
    this.drained.length = 0
    this.spoke.length = 0
    this.sounded.length = 0
    this.drainCursor = this.game.drainedAt.length
    this.logCursor = this.game.log.length
    this.display.render(this.game, this.game.step)
  }

  /** Is a recording playing over the top of this session's own game? */
  get watchingGhost(): boolean {
    return this.ghostTrace !== undefined
  }

  /**
   * The first step at which this client's re-simulation disagreed with the recorded one, or −1.
   *
   * 10 §3's `divergent` outcome, seen live. A spectator that diverges has not caught anybody
   * cheating — it has caught *us*, because both ends are running the same trace through the same
   * core (PH-088). Worth surfacing rather than swallowing for exactly that reason.
   */
  get ghostDivergedAt(): number {
    return this.ghostDiverged
  }

  /** How far through the recording playback is, and how long it is. */
  get ghostProgress(): { readonly step: number; readonly steps: number } {
    return { step: this.ghostStep, steps: this.ghostTrace?.steps ?? 0 }
  }

  /**
   * Advance playback by a frame of wall-clock, scaled by the playback speed.
   *
   * `speed` is a multiplier on real time: 1 is the pace the game was played at, 0.25 is a quarter
   * of it, 0 is paused. Applied to the elapsed *time* rather than to a step count, so playback at
   * half speed on a 120 Hz screen and on a 30 Hz one advance the simulation identically — the
   * frame rate decides how often the picture is drawn and never how fast the ball moves.
   */
  ghostFrame(elapsedSeconds: number, speed: number): SessionFrame {
    this.drained.length = 0
    this.spoke.length = 0
    this.sounded.length = 0

    const trace = this.ghostTrace
    if (trace === undefined) {
      return { stepsRun: 0, stepsDropped: 0, alpha: 0, paused: true, interruption: '' }
    }

    this.ghostRemainder += Math.max(0, elapsedSeconds * speed * STEP_HZ)
    const whole = Math.floor(this.ghostRemainder)
    this.ghostRemainder -= whole
    const wanted = Math.min(trace.steps, this.ghostStep + whole)
    const stepsRun = this.runGhostTo(wanted)

    this.display.render(this.game, this.game.step)
    this.collectNotes()
    this.collectDrains()
    return {
      stepsRun,
      stepsDropped: 0,
      // No interpolation: playback advances a whole number of steps per frame and the renderer is
      // handed the state it produced. An alpha here would smooth between two frames of a
      // simulation the viewer is already choosing the pace of.
      alpha: 0,
      paused: speed === 0 || this.ghostStep >= trace.steps,
      interruption: '',
    }
  }

  /**
   * Jump to a step.
   *
   * Forwards is simply running on. Backwards restores the nearest earlier snapshot and runs from
   * there, which is why the marks exist — the alternative is re-simulating from the beginning, and
   * a scrub bar that freezes the page for a second every time it is dragged is a scrub bar nobody
   * touches twice.
   */
  seekGhost(step: number): void {
    const trace = this.ghostTrace
    if (trace === undefined) return
    const target = Math.max(0, Math.min(Math.round(step), trace.steps))
    // A jump discards the fraction. Carrying it would add a step or two to wherever the viewer
    // landed, which is a scrub bar that never quite goes where it is dragged.
    this.ghostRemainder = 0
    if (target < this.ghostStep) {
      let best = 0
      for (const mark of this.ghostMarks.keys()) if (mark <= target && mark > best) best = mark
      const snapshot = this.ghostMarks.get(best)
      if (snapshot !== undefined) {
        this.game.restore(snapshot)
        this.lighting.reset()
        this.ghostStep = best
        this.ghostEvent = eventIndexAt(trace, best)
      }
    }
    this.runGhostTo(target)
    // Everything the jump produced along the way is thrown out, for the same reason a resume
    // throws it out: a scrub must not fire nine minutes of callouts and lamp events on arrival.
    this.game.drainEffects()
    this.events.clear()
    this.contacts.clear()
    this.drained.length = 0
    this.spoke.length = 0
    this.sounded.length = 0
    this.drainCursor = this.game.drainedAt.length
    this.logCursor = this.game.log.length
    this.display.render(this.game, this.game.step)
    this.syncLighting(true)
  }

  /** Run playback forward to a step, marking the way. Returns how many steps were run. */
  private runGhostTo(target: number): number {
    const trace = this.ghostTrace
    if (trace === undefined || target <= this.ghostStep) return 0
    const from = this.ghostStep
    const result = replayTrace(this.game, trace, {
      fromStep: this.ghostStep,
      fromEvent: this.ghostEvent,
      untilStep: target,
      ...(this.ghostVerify ? { verify: true } : {}),
    })
    // **First disagreement wins and is kept.** A divergence is not a transient: once two runs of
    // the same trace differ, everything after it differs too, so the useful number is where it
    // started and a later chunk must not overwrite it.
    if (this.ghostDiverged < 0 && result.divergedAt >= 0) this.ghostDiverged = result.divergedAt
    this.ghostStep = result.steps
    this.ghostEvent = result.nextEvent
    // Marked on the boundary rather than wherever the frame happened to land, so two viewers that
    // scrubbed differently share the same marks and the memory used is bounded by the length of
    // the recording rather than by how long somebody watched it.
    const mark = this.ghostStep - (this.ghostStep % GHOST_SNAPSHOT_STEPS)
    if (mark > 0 && !this.ghostMarks.has(mark) && this.ghostStep === mark) {
      this.ghostMarks.set(mark, this.game.snapshot())
    }
    return this.ghostStep - from
  }

  /**
   * Pause a game that is only waiting for the player, once it has waited long enough. Waiting
   * means initials are asked for, or a ball is in the shooter lane with no other ball live and no
   * idle auto-plunger to serve it.
   */
  private holdIfNobodyIsThere(game: Game, elapsedSeconds: number): void {
    if (this.driver.isPaused) {
      if (!this.heldForThePlayer) this.waitedFor = 0
      return
    }
    this.heldForThePlayer = false
    const waiting =
      game.phase === 'highScoreEntry' ||
      (game.phase === 'ballInPlay' &&
        game.awaitingLaunch &&
        this.ballsInPlay <= 1 &&
        game.idleAutoPlungeSteps <= 0)
    const limit = this.options.waitingHoldSeconds ?? DEFAULT_WAITING_HOLD_SECONDS
    if (!waiting || limit <= 0) {
      this.waitedFor = 0
      return
    }
    this.waitedFor += elapsedSeconds
    if (this.waitedFor < limit) return
    this.waitedFor = 0
    this.driver.pause()
    this.heldForThePlayer = true
    this.holds++
    this.interruption = this.holdNotice()
    this.note(
      `held — ${game.phase === 'highScoreEntry' ? 'initials asked for' : 'a ball in the lane'}` +
        ` and nothing touched for ${limit} s, so the game stops counting steps until the player is back (step ${game.step})`,
    )
  }

  /** What a held game says while it waits; a hook, because the controls are the host's. */
  protected holdNotice(): string {
    return 'Paused while you were away. Press any control to carry on.'
  }

  /** Seconds of ball save left. */
  get ballSaveSeconds(): number {
    return this.game.ballSaveSteps / STEP_HZ
  }

  get ballsInPlay(): number {
    let n = 0
    for (const b of this.loaded.world.balls) if (b.active) n++
    return n
  }

  /** Fastest ball on the playfield, m/s. The single most useful number while tuning. */
  get fastestBall(): number {
    let fastest = 0
    for (const b of this.loaded.world.balls) {
      if (b.active && b.speed() > fastest) fastest = b.speed()
    }
    return fastest
  }

  /**
   * How fast time runs. One, for a player.
   *
   * A hook rather than a field, because the only thing that has ever wanted another answer is
   * the harness's slow motion — and a *player* with a speed control is a player with a way to
   * make every skill shot trivial and every leaderboard score meaningless.
   */
  protected timeScale(_controls: PlayControls): number {
    return 1
  }

  /** Extra work after a rebuild. The harness re-applies its bat tuning here. */
  protected afterReset(): void {}

  /**
   * How the player is told to start the machine again after it stopped itself.
   *
   * A hook, because the *affordance* is the client's and the *fact* is the session's. The
   * harness has a pause key; the shell has a button in the corner of the playfield; a headset
   * has neither. Naming one of them here would put a key that does not exist on that surface
   * into the one message a player reads at the moment something has gone wrong.
   */
  protected resumeHint(): string {
    return 'Press pause to continue.'
  }

  /**
   * Turn a frame of held controls into what the game is told.
   *
   * Overridden by the harness to add its own; everything a player can do is here.
   */
  protected applyInput(controls: PlayControls): void {
    const game = this.game

    // Edge-detected. The bats need both edges — a bat told only about presses never returns —
    // but the cabinet switch behind the button must close once per press, and `pressButton` is
    // what knows the difference.
    // What a button *does* is the machine's business, not this layer's: `pressButton` knows that
    // during initials entry the flippers step the alphabet, because a server re-simulating this
    // trace has a `Game` and nothing else.
    if (controls.leftFlipper !== this.leftHeld) {
      this.leftHeld = controls.leftFlipper
      this.record({ kind: 'flipper', side: 'left', down: controls.leftFlipper })
      game.pressButton('left', controls.leftFlipper)
    }
    if (controls.rightFlipper !== this.rightHeld) {
      this.rightHeld = controls.rightFlipper
      this.record({ kind: 'flipper', side: 'right', down: controls.rightFlipper })
      game.pressButton('right', controls.rightFlipper)
    }

    for (const [ix, iy] of controls.nudges) {
      this.record({ kind: 'nudge', ix, iy })
      this.loaded.world.nudge(ix, iy)
    }
    controls.nudges.length = 0

    if (controls.launch) {
      controls.launch = false
      // **A plunge on a paused machine plays it** (25 Sep 2026, the build-86 round: "plunger worked
      // at the beginning but stopped working while playing"). The headset pauses the machine when
      // it comes off a head or loses focus — the system menu, the Guardian — and the only way back
      // was the start button. A player who comes back and pulls the plunger is asking to play; the
      // plunge used to be taken by a frozen world and the ball sat in the lane. Not recorded, like
      // the start button's resume: a pause is the client's, not the game's.
      if (this.driver.isPaused) {
        this.driver.resume()
        this.interruption = ''
        this.note('resumed by the plunger')
      }
      if (game.launchWouldStart()) {
        this.startGame(controls.launchPower)
      } else {
        this.record({ kind: 'launch', power: controls.launchPower })
        this.pressLaunch(controls.launchPower)
      }
    }

    // **The start button on the lockdown bar** (06 §2.4). Every role but the plunger's: a start
    // button is not a plunger, and routed straight through it would serve a waiting ball at zero
    // power. Recorded as a `launch` because it *is* `pressLaunchButton` — the one core call that
    // starts a game — and inventing an action for a press that resolves to an existing one would
    // be a contract change bought for nothing.
    //
    // **The exclusion was written as `attract || isOver` and excluded a third thing nobody meant
    // to exclude** (M8-27). `isOver` is `phase === 'gameOver' && initialsFor < 0`, so it is false
    // throughout high-score entry — and `pressLaunch`'s own summary below says the button is "a
    // plunger when a ball is waiting, the start button when the game is over, and the enter key
    // during initials entry". Three roles named, one wired. On a cabinet the start button *is*
    // the enter key: the flippers step the alphabet and start takes the letter.
    //
    // Found on the Quest, where this is a button a player can actually see and press — it sat in
    // high-score entry for four and a half minutes taking fourteen presses and doing nothing —
    // and fixed here in the same change because `cabinetRig` raises this latch in WebXR too and
    // the two clients are held identical.
    //
    // **Strictly the three roles `pressLaunchButton` names, minus the plunger.** The tempting
    // rewrite is a value test — "would this plunge a waiting ball?" — which reads better and was
    // written first. A probe rejected it: it also stops the button in `gameOver` while
    // `awaitingLaunch` is true, and `endGame` deactivates the balls without clearing the
    // launcher's held one. Adding a phase is additive and cannot regress the case that already
    // worked; narrowing to a value could.
    //
    // High-score entry is safe to add because `pressLaunchButton` checks `highScoreEntry`
    // *before* `awaitingLaunch`, so a letter is taken rather than a waiting ball served.
    if (controls.start) {
      controls.start = false
      if (this.driver.isPaused) {
        // The way back from a performance interruption, which is what `resumeHint` names.
        this.driver.resume()
        this.interruption = ''
        this.note('resumed')
      } else if (game.launchWouldStart()) {
        this.startGame(0)
      } else if (game.phase === 'highScoreEntry' || game.phase === 'attract' || game.isOver) {
        this.record({ kind: 'launch', power: 0 })
        this.pressLaunch(0)
      }
    }

    // **The coin door** (06 §2.4). The clunk has fired since M5's cabinet rig and the credit had
    // nowhere to go. Recorded, because `credits` is inside `game.fingerprint` and therefore in
    // every checkpoint hash: a credit added silently is a hash a server re-simulating the same
    // trace cannot reproduce, and the score it refuses is an honest one.
    if (controls.coin) {
      controls.coin = false
      this.record({ kind: 'credit' })
      game.addCredit()
    }

    // **Recorded before it is applied, like every other control here**, and recorded even when the
    // machine is not asking: `Game.initialsSkip` is a no-op outside `highScoreEntry`, so a stray
    // press is a stray press in the replay too. Dropping it here instead would make the trace and
    // the game disagree about what the player did, which is the one thing a recorder may not do.
    if (controls.skipInitials) {
      controls.skipInitials = false
      this.record({ kind: 'skip' })
      game.initialsSkip()
    }
  }

  /**
   * The launch button, which is three buttons on a real machine and one key here.
   *
   * A plunger when a ball is waiting, the start button when the game is over, and the enter key
   * during initials entry — which is exactly how a machine with a button-plunger wires them,
   * because it is the only button in front of the player.
   */
  protected pressLaunch(power: number): void {
    if (this.game.pressLaunchButton(power) === 'nothing') this.note('nothing in the shooter lane')
  }

  /**
   * Hand one player action to whoever is recording (M3-39).
   *
   * Stamped with the **game's** current step, not a frame number and not a clock. Input is
   * sampled by the simulation, so an action lands on an exact step; a replay applies it on that
   * step and reaches the identical game. Every action a player can take goes through
   * `applyInput`, which is why this is the only place that has to call it.
   */
  private record(action: TraceAction): void {
    this.options.onAction?.(this.game.step, action)
  }

  protected note(message: string): void {
    this.notes.unshift(message)
    if (this.notes.length > MAX_NOTES) this.notes.length = MAX_NOTES
  }

  private newGame(): Game {
    const game = new Game(this.loaded.definition, this.loaded.world, {
      playerCount: this.options.playerCount ?? 1,
      seed: this.options.seed ?? 20260817,
      ...(this.options.startingInitials === undefined
        ? {}
        : { startingInitials: this.options.startingInitials }),
    })
    return game
  }

  /**
   * Which balls left the playfield this frame.
   *
   * `Game.drainedAt` only ever grows within a game, so a cursor over it is exact — and it
   * carries the position, which is what a drain sound needs to be panned to the outlane the ball
   * actually went down rather than to the middle of the machine.
   */
  private collectDrains(): void {
    const all = this.game.drainedAt
    for (let i = this.drainCursor; i < all.length; i++) this.drained.push(all[i]![0])
    this.drainCursor = all.length
  }

  /** Move everything the game said since the last frame onto the notice list. */
  private collectNotes(): void {
    const log = this.game.log
    for (; this.logCursor < log.length; this.logCursor++) this.note(log[this.logCursor]!)
    // Drained rather than read: the game keeps every effect it has ever raised otherwise, and a
    // machine left switched on raises thousands an hour.
    for (const effect of this.game.drainEffects()) {
      this.lighting.cue(effect.id, effect.step)
      // A `display` effect is a rule asking for a scene, and until M3-17 nothing was listening:
      // the player and all 23 of NEON NOVA's scenes existed and were driven by nobody, so the
      // machine's display was a black rectangle painted on the backglass.
      if (effect.kind === 'display' && !this.display.request(effect.id, this.game.step)) {
        this.note(`display ${effect.id} — no such scene`)
        continue
      }
      // A `speech` effect is a rule asking the machine to say something, and until M3-27 the ten
      // callouts NEON NOVA declares reached a log line and nothing else — the same shape the
      // display was in.
      if (effect.kind === 'speech') this.spoke.push(effect.id)
      if (effect.kind === 'sound') this.sounded.push(effect.id)
      this.note(
        effect.value !== 0 ? `${effect.kind} ${effect.id} ${effect.value}` : `${effect.kind} ${effect.id}`,
      )
    }
    this.syncLighting()
  }
  private syncLighting(prime = false): void {
    const context = { step: this.game.step, attract: this.game.phase === 'attract' || this.game.isOver,
      tilted: this.game.tilt.tilted || this.game.tilt.slammed, balls: this.ballsInPlay,
      mode: (this.loaded.definition.rules?.modes ?? []).find(mode => this.game.engine.isModeActive(mode.id))?.id ?? '' }
    if (prime) this.lighting.prime(context)
    else this.lighting.advance(context)
    for (const [id, device] of this.loaded.devices) {
      if (device.kind === 'kicker') this.lighting.held(id, (device as Kicker).heldBall >= 0)
    }
  }
}
